import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Code2, LayoutList } from "lucide-react";

import { Alert } from "../../components/Alert/Alert";
import { Button } from "../../components/Button/Button";
import { ChipInput } from "../../components/ChipInput/ChipInput";
import { Input } from "../../components/Input/Input";
import { Modal } from "../../components/Modal/Modal";
import { MetaSchemaEditor } from "../../components/MetaSchemaEditor/MetaSchemaEditor";
import { NumberInputWithUnit } from "../../components/NumberInputWithUnit/NumberInputWithUnit";
import { PageHeader } from "../../components/PageHeader/PageHeader";
import { SchemaEditor } from "../../components/SchemaEditor/SchemaEditor";
import { SegmentedToggle } from "../../components/SegmentedToggle/SegmentedToggle";
import { Switch } from "../../components/Switch/Switch";
import { Textarea } from "../../components/Textarea/Textarea";
import { useToast } from "../../components/Toast/Toast";
import { Card } from "../../components/Card/Card";
import { ApiError, describeError } from "../../lib/errors";
import { getProject, patchProject } from "./api";
import { useConfigOutline } from "../../app/ConfigOutline";
import { SECTION_META, SECTION_ORDER } from "./config/types";
import { FieldRow } from "./config/FieldRow";
import { SectionCard } from "./config/SectionCard";
import { StickySaveBar } from "./config/StickySaveBar";
import { DEFAULT_CONFIG, normalizeConfig } from "./config/defaults";
import {
  countChanges,
  describeJsonError,
  humanizeDuration,
  isEqual,
  isSectionDirty,
  rangeError,
} from "./config/utils";
import type {
  ChatConfig,
  GuildConfig,
  HelpRequestConfig,
  ProfileConfig,
  ProjectConfig,
  RateLimitsConfig,
  GuildAssetsConfig,
} from "./config/types";
import styles from "./ProjectConfigPage.module.css";

type Mode = "forms" | "json";

const TYPE_REGEX = /^[a-z][a-z0-9_]*$/;
const AVATAR_REGEX = /^[a-z0-9_]+$/i;
const META_KEY_REGEX = /^[a-z_][a-z0-9_]*$/;

/**
 * Helper line under the meta-schema editor. Spells out the empty case,
 * because it reads the opposite way to its neighbours: an empty
 * `stats_schema` accepts any object and an empty `allowed_avatars` accepts
 * any string, but an empty `meta_schema` accepts nothing at all.
 */
function metaSchemaHelper(schema: ProfileConfig["meta_schema"]): string {
  if (schema.length === 0) {
    return "Empty schema — players.meta stays {} and PATCH /v1/me/meta rejects every key. Unlike stats_schema, empty here does not mean “accept anything”.";
  }
  const noun = schema.length === 1 ? "field" : "fields";
  return `${schema.length} ${noun} · players write them via PATCH /v1/me/meta`;
}

export function ProjectConfigPage() {
  const { id = "" } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const { push } = useToast();
  const { setOutline } = useConfigOutline();

  const { data, refetch, isLoading } = useQuery({
    queryKey: ["project", id, "config"],
    queryFn: () =>
      getProject(id).then((r) => ({
        project: r.data,
        etag: r.etag ?? r.data.etag,
      })),
    enabled: !!id,
  });

  // Server snapshot. Stays in sync with the last successful PATCH or the
  // initial GET. workingConfig is what the user is editing — dirty = diff.
  const [serverConfig, setServerConfig] = useState<ProjectConfig>(
    DEFAULT_CONFIG,
  );
  const [workingConfig, setWorkingConfig] = useState<ProjectConfig>(
    DEFAULT_CONFIG,
  );
  const [mode, setMode] = useState<Mode>("forms");
  const [rawJson, setRawJson] = useState("");
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [conflictOpen, setConflictOpen] = useState(false);
  // Scroll-spy: which section is currently in view (drives the sidebar sub-nav).
  const [activeSection, setActiveSection] = useState<string>(SECTION_ORDER[0]);

  // Bootstrap working/server state from the loaded project. The project Get
  // response carries the raw config JSON object, typed on ProjectDetail.
  useEffect(() => {
    if (!data) return;
    const cfg = normalizeConfig(data.project.config);
    setServerConfig(cfg);
    setWorkingConfig(cfg);
    setRawJson(JSON.stringify(cfg, null, 2));
    setJsonError(null);
    setSaveError(null);
  }, [data?.etag, data?.project.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Whenever the user is on "forms" mode, keep the raw JSON pre-rendered so
  // toggling to "json" shows the live edits without needing a manual sync.
  useEffect(() => {
    if (mode === "forms") {
      setRawJson(JSON.stringify(workingConfig, null, 2));
      setJsonError(null);
    }
  }, [mode, workingConfig]);

  const dirty = useMemo(() => {
    if (!isEqual(serverConfig, workingConfig)) return true;
    // Raw JSON edits show up in `rawJson` only when parse fails — in that
    // case workingConfig isn't updated but the user clearly has unsaved
    // text. Mockup L824: bar visible with "Invalid JSON…" so user can
    // still see Discard.
    if (mode === "json" && jsonError != null) {
      return rawJson !== JSON.stringify(serverConfig, null, 2);
    }
    return false;
  }, [serverConfig, workingConfig, mode, jsonError, rawJson]);

  const { changes, sections } = useMemo(
    () => countChanges(serverConfig, workingConfig),
    [serverConfig, workingConfig],
  );

  // Field-level validation. Save disabled when any field is invalid.
  const fieldErrors = useMemo(
    () => validateConfig(workingConfig),
    [workingConfig],
  );
  const hasFieldError = useMemo(
    () => Object.values(fieldErrors).some((v) => v != null),
    [fieldErrors],
  );

  // beforeunload guard — only attached while dirty so unaffected tabs are
  // free to navigate without confirm dialogs. react-router v6 useBlocker
  // would be nicer but isn't available on this version yet.
  useEffect(() => {
    if (!dirty) return;
    function onBeforeUnload(e: BeforeUnloadEvent) {
      e.preventDefault();
      e.returnValue = "";
    }
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  // Scroll-spy — observe the section cards and track which one is in view.
  // Drives the active state of the sidebar sub-nav (mockup L355 `.sub-item.active`).
  useEffect(() => {
    if (mode !== "forms" || isLoading) return;
    if (typeof IntersectionObserver === "undefined") return;
    const els = SECTION_ORDER.map((key) =>
      document.getElementById(key),
    ).filter((el): el is HTMLElement => el != null);
    if (els.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort(
            (a, b) =>
              a.boundingClientRect.top - b.boundingClientRect.top,
          );
        if (visible[0]) setActiveSection(visible[0].target.id);
      },
      { rootMargin: "-80px 0px -55% 0px" },
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [mode, isLoading]);

  // Publish the section outline to the AppShell sidebar sub-nav. Cleared on
  // unmount / when leaving forms mode so the sub-nav only shows on this page.
  useEffect(() => {
    if (mode !== "forms" || isLoading) {
      setOutline({ sections: [], activeId: "" });
      return;
    }
    setOutline({
      sections: SECTION_ORDER.map((key) => ({
        id: key,
        label: SECTION_META[key].title,
        dirty: isSectionDirty(serverConfig, workingConfig, key),
      })),
      activeId: activeSection,
    });
  }, [
    mode,
    isLoading,
    serverConfig,
    workingConfig,
    activeSection,
    setOutline,
  ]);

  useEffect(
    () => () => setOutline({ sections: [], activeId: "" }),
    [setOutline],
  );

  function setChat<K extends keyof ChatConfig>(key: K, value: ChatConfig[K]) {
    setWorkingConfig((prev) => ({ ...prev, chat: { ...prev.chat, [key]: value } }));
  }
  function setGuild<K extends keyof GuildConfig>(
    key: K,
    value: GuildConfig[K],
  ) {
    setWorkingConfig((prev) => ({
      ...prev,
      guild: { ...prev.guild, [key]: value },
    }));
  }
  function setHelp<K extends keyof HelpRequestConfig>(
    key: K,
    value: HelpRequestConfig[K],
  ) {
    setWorkingConfig((prev) => ({
      ...prev,
      help_request: { ...prev.help_request, [key]: value },
    }));
  }
  function setRate<K extends keyof RateLimitsConfig>(
    key: K,
    value: RateLimitsConfig[K],
  ) {
    setWorkingConfig((prev) => ({
      ...prev,
      rate_limits: { ...prev.rate_limits, [key]: value },
    }));
  }
  function setProfile<K extends keyof ProfileConfig>(
    key: K,
    value: ProfileConfig[K],
  ) {
    setWorkingConfig((prev) => ({
      ...prev,
      profile: { ...prev.profile, [key]: value },
    }));
  }
  function setGuildAssets<K extends keyof GuildAssetsConfig>(
    key: K,
    value: GuildAssetsConfig[K],
  ) {
    setWorkingConfig((prev) => ({
      ...prev,
      guild_assets: { ...prev.guild_assets, [key]: value },
    }));
  }

  function handleRawChange(next: string) {
    setRawJson(next);
    if (next.trim() === "") {
      setJsonError("Required");
      return;
    }
    try {
      const parsed = JSON.parse(next);
      setJsonError(null);
      // Normalize so partial JSON doesn't lose required keys.
      setWorkingConfig(normalizeConfig(parsed));
    } catch (err) {
      setJsonError(describeJsonError(next, err));
    }
  }

  async function doSave() {
    if (!data) return;
    if (mode === "json" && jsonError) return;
    if (hasFieldError) return;
    setSaving(true);
    setSaveError(null);
    try {
      const rawEtag = (data.etag ?? "")
        .replace(/^W\//, "")
        .replace(/^"|"$/g, "");
      const r = await patchProject(
        id,
        { config: workingConfig },
        rawEtag,
      );
      const next = normalizeConfig(r.data.config ?? workingConfig);
      setServerConfig(next);
      setWorkingConfig(next);
      setRawJson(JSON.stringify(next, null, 2));
      qc.setQueryData(["project", id, "config"], {
        project: r.data,
        etag: r.etag ?? r.data.etag,
      });
      await qc.invalidateQueries({ queryKey: ["projects"] });
      await qc.invalidateQueries({ queryKey: ["project", id, "activity"] });
      push({ variant: "success", message: "Config saved." });
    } catch (err) {
      if (err instanceof ApiError && err.code === "etag_mismatch") {
        // v1: a generic "reload" modal. Mockup L803 specifies a diff modal
        // surfacing author + timestamp — deferred until backend exposes
        // last-editor metadata.
        setConflictOpen(true);
      } else if (err instanceof ApiError) {
        setSaveError(describeError(err.code));
      } else {
        setSaveError(describeError("internal"));
      }
    } finally {
      setSaving(false);
    }
  }

  function confirmDiscard() {
    setWorkingConfig(serverConfig);
    setRawJson(JSON.stringify(serverConfig, null, 2));
    setJsonError(null);
    setSaveError(null);
    setDiscardOpen(false);
  }

  async function handleConflictReload() {
    setConflictOpen(false);
    await refetch();
  }

  const breadcrumb = [
    { label: "All projects", to: "/projects" },
    { label: data?.project.name ?? "Project" },
  ];

  const actions = (
    <SegmentedToggle<Mode>
      aria-label="Edit mode"
      value={mode}
      onChange={setMode}
      options={[
        { value: "forms", label: "Forms", icon: <LayoutList size={12} /> },
        { value: "json", label: "Raw JSON", icon: <Code2 size={12} /> },
      ]}
    />
  );

  const header = (
    <PageHeader
      breadcrumb={breadcrumb}
      title="Configuration"
      actions={actions}
    />
  );

  if (isLoading || !data) {
    return (
      <>
        {header}
        <div className={styles.page}>Loading…</div>
      </>
    );
  }

  const saveDisabled =
    !dirty || hasFieldError || (mode === "json" && jsonError != null);

  return (
    <>
      {header}
      <div className={styles.page}>
        <div className={styles.grid}>
          {saveError && (
            <Alert variant="danger" title="Couldn't save">
              {saveError}
            </Alert>
          )}

          {mode === "json" ? (
            <Card className={styles.jsonCard}>
              <div className={styles.jsonHeader}>
                <h3 className={styles.jsonTitle}>Raw JSON</h3>
                <span className={styles.jsonSub}>games.config</span>
              </div>
              <div className={styles.jsonBody}>
                {jsonError && (
                  <Alert variant="danger" title="Invalid JSON">
                    {jsonError}
                  </Alert>
                )}
                <Textarea
                  aria-label="Raw config JSON"
                  value={rawJson}
                  onChange={(e) => handleRawChange(e.target.value)}
                  rows={28}
                  spellCheck={false}
                  className={styles.jsonTextarea}
                />
              </div>
            </Card>
          ) : (
            <FormsBody
              workingConfig={workingConfig}
              serverConfig={serverConfig}
              fieldErrors={fieldErrors}
              setChat={setChat}
              setGuild={setGuild}
              setHelp={setHelp}
              setRate={setRate}
              setProfile={setProfile}
              setGuildAssets={setGuildAssets}
            />
          )}
        </div>

        <StickySaveBar
          visible={dirty}
          changes={changes}
          sections={sections}
          disabled={saveDisabled}
          saving={saving}
          onDiscard={() => setDiscardOpen(true)}
          onSave={doSave}
        />
      </div>

      <Modal
        open={discardOpen}
        onClose={() => setDiscardOpen(false)}
        title="Discard unsaved changes?"
        footer={
          <>
            <Button variant="secondary" onClick={() => setDiscardOpen(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={confirmDiscard}>
              Discard
            </Button>
          </>
        }
      >
        <p>
          You will lose {changes} {changes === 1 ? "change" : "changes"} across{" "}
          {sections} {sections === 1 ? "section" : "sections"}. This can't be
          undone.
        </p>
      </Modal>

      <Modal
        open={conflictOpen}
        onClose={() => setConflictOpen(false)}
        title="Config changed by someone else"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setConflictOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleConflictReload}>Reload page</Button>
          </>
        }
      >
        <p>
          Another user saved a newer version of this config. Reload to load
          their changes — your current edits will be discarded.
        </p>
      </Modal>
    </>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Forms body — split out so the JSON mode can swap it cleanly without
// touching the save bar / modals around it.
// ──────────────────────────────────────────────────────────────────────

type FieldErrors = Partial<Record<FieldKey, string>>;

type FieldKey =
  | "chat.history_window"
  | "chat.max_message_length"
  | "chat.rate_limit_per_minute"
  | "guild.max_members"
  | "help_request.default_ttl_seconds"
  | "help_request.max_active_per_player"
  | "rate_limits.auth_per_ip_per_minute"
  | "rate_limits.help_create_per_minute"
  | "profile.meta_schema";

interface FormsBodyProps {
  workingConfig: ProjectConfig;
  serverConfig: ProjectConfig;
  fieldErrors: FieldErrors;
  setChat: <K extends keyof ChatConfig>(key: K, value: ChatConfig[K]) => void;
  setGuild: <K extends keyof GuildConfig>(
    key: K,
    value: GuildConfig[K],
  ) => void;
  setHelp: <K extends keyof HelpRequestConfig>(
    key: K,
    value: HelpRequestConfig[K],
  ) => void;
  setRate: <K extends keyof RateLimitsConfig>(
    key: K,
    value: RateLimitsConfig[K],
  ) => void;
  setProfile: <K extends keyof ProfileConfig>(
    key: K,
    value: ProfileConfig[K],
  ) => void;
  setGuildAssets: <K extends keyof GuildAssetsConfig>(
    key: K,
    value: GuildAssetsConfig[K],
  ) => void;
}

function FormsBody({
  workingConfig,
  serverConfig,
  fieldErrors,
  setChat,
  setGuild,
  setHelp,
  setRate,
  setProfile,
  setGuildAssets,
}: FormsBodyProps) {
  // Track chips added during this editing session so we can highlight them
  // with the warn-soft variant (mockup L602 shows `energy` styled this way).
  const newlyChatWords = useNewlyAdded(
    serverConfig.chat.profanity_words,
    workingConfig.chat.profanity_words,
  );
  const newlyHelpTypes = useNewlyAdded(
    serverConfig.help_request.types,
    workingConfig.help_request.types,
  );

  const isModified = (a: unknown, b: unknown) => !isEqual(a, b);

  return (
    <>
      {/* ───────── Chat & profanity ───────── */}
      <SectionCard
        id="chat"
        title="Chat & profanity"
        apiName="games.config.chat"
        dirty={isSectionDirty(serverConfig, workingConfig, "chat")}
        description="Per-game settings for guild chat: history window, message limits, and the profanity filter that runs on every message before publish."
      >
        <FieldRow
          name="History window"
          apiName="chat.history_window"
          hint="How many recent messages per guild are retained. A background worker truncates excess every 15 minutes."
        >
          <NumberInputWithUnit
            unit="msgs"
            min={50}
            max={5000}
            value={workingConfig.chat.history_window}
            onChange={(v) => setChat("history_window", v)}
            modified={isModified(
              serverConfig.chat.history_window,
              workingConfig.chat.history_window,
            )}
            error={fieldErrors["chat.history_window"]}
            aria-label="History window"
          />
        </FieldRow>
        <FieldRow
          name="Max message length"
          apiName="chat.max_message_length"
          hint="Backend rejects chat.send if the body is longer than this."
        >
          <NumberInputWithUnit
            unit="chars"
            min={1}
            max={2000}
            value={workingConfig.chat.max_message_length}
            onChange={(v) => setChat("max_message_length", v)}
            modified={isModified(
              serverConfig.chat.max_message_length,
              workingConfig.chat.max_message_length,
            )}
            error={fieldErrors["chat.max_message_length"]}
            aria-label="Max message length"
          />
        </FieldRow>
        <FieldRow
          name="Rate limit per player"
          apiName="chat.rate_limit_per_minute"
          hint="How many messages per minute a player can send. Sliding window in Redis."
        >
          <NumberInputWithUnit
            unit="/min"
            min={1}
            max={600}
            value={workingConfig.chat.rate_limit_per_minute}
            onChange={(v) => setChat("rate_limit_per_minute", v)}
            modified={isModified(
              serverConfig.chat.rate_limit_per_minute,
              workingConfig.chat.rate_limit_per_minute,
            )}
            error={fieldErrors["chat.rate_limit_per_minute"]}
            aria-label="Rate limit per player"
          />
        </FieldRow>
        <FieldRow
          name="Profanity words"
          apiName="chat.profanity_words"
          hint="Custom list merged with the platform default set. Case-insensitive, whole-word match. Enter to add, × to remove."
        >
          <ChipInput
            value={workingConfig.chat.profanity_words}
            onChange={(next) => setChat("profanity_words", next)}
            placeholder="Add word…"
            newlyAdded={newlyChatWords}
            modified={isModified(
              serverConfig.chat.profanity_words,
              workingConfig.chat.profanity_words,
            )}
            helperText={`${workingConfig.chat.profanity_words.length} custom ${
              workingConfig.chat.profanity_words.length === 1
                ? "word"
                : "words"
            }`}
          />
        </FieldRow>
      </SectionCard>

      {/* ───────── Guild defaults ───────── */}
      <SectionCard
        id="guild"
        title="Guild defaults"
        apiName="games.config.guild"
        dirty={isSectionDirty(serverConfig, workingConfig, "guild")}
        description="Defaults applied when a new guild is created. Existing guilds are not retroactively updated."
      >
        <FieldRow
          name="Max members"
          apiName="guild.max_members"
          hint="Player cap per guild."
        >
          <NumberInputWithUnit
            unit="players"
            min={1}
            max={1000}
            value={workingConfig.guild.max_members}
            onChange={(v) => setGuild("max_members", v)}
            modified={isModified(
              serverConfig.guild.max_members,
              workingConfig.guild.max_members,
            )}
            error={fieldErrors["guild.max_members"]}
            aria-label="Max members"
          />
        </FieldRow>
        <FieldRow
          name="Require unique name"
          apiName="guild.require_unique_name"
          hint="When enabled, the backend rejects guild creates with a duplicate name (case-insensitive)."
        >
          <Switch
            checked={workingConfig.guild.require_unique_name}
            onChange={(next) => setGuild("require_unique_name", next)}
            label={
              workingConfig.guild.require_unique_name
                ? "On — names must be unique (case-insensitive)"
                : "Off — duplicate names allowed"
            }
          />
        </FieldRow>
        <FieldRow
          name="Metadata schema"
          apiName="guild.metadata_schema"
          hint="Custom fields you can write to guilds.metadata (JSONB). Used by search filters."
        >
          <SchemaEditor
            value={workingConfig.guild.metadata_schema}
            onChange={(next) => setGuild("metadata_schema", next)}
          />
        </FieldRow>
      </SectionCard>

      {/* ───────── Help requests ───────── */}
      <SectionCard
        id="help_request"
        title="Help requests"
        apiName="games.config.help_request"
        dirty={isSectionDirty(serverConfig, workingConfig, "help_request")}
        description="In-guild help requests such as “need cards” or “need lives”. Per-game configuration."
      >
        <FieldRow
          name="Allowed types"
          apiName="help_request.types"
          hint="Which request_type values the API accepts. Used by the game client as an enum."
        >
          <ChipInput
            value={workingConfig.help_request.types}
            onChange={(next) => setHelp("types", next)}
            placeholder="Add type…"
            newlyAdded={newlyHelpTypes}
            modified={isModified(
              serverConfig.help_request.types,
              workingConfig.help_request.types,
            )}
            validate={(chip) =>
              TYPE_REGEX.test(chip)
                ? null
                : "Lowercase letters, digits, underscore"
            }
          />
        </FieldRow>
        <FieldRow
          name="Default TTL"
          apiName="help_request.default_ttl_seconds"
          hint="Applied if the game client doesn't provide one."
        >
          <NumberInputWithUnit
            unit="sec"
            min={60}
            max={86400}
            value={workingConfig.help_request.default_ttl_seconds}
            onChange={(v) => setHelp("default_ttl_seconds", v)}
            modified={isModified(
              serverConfig.help_request.default_ttl_seconds,
              workingConfig.help_request.default_ttl_seconds,
            )}
            error={fieldErrors["help_request.default_ttl_seconds"]}
            helperText={`= ${humanizeDuration(
              workingConfig.help_request.default_ttl_seconds,
            )}`}
            aria-label="Default TTL"
          />
        </FieldRow>
        <FieldRow
          name="Max active per player"
          apiName="help_request.max_active_per_player"
          hint="How many simultaneously open help requests a player can have."
        >
          <Input
            type="number"
            min={1}
            max={50}
            value={
              Number.isNaN(workingConfig.help_request.max_active_per_player)
                ? ""
                : workingConfig.help_request.max_active_per_player
            }
            onChange={(e) =>
              setHelp(
                "max_active_per_player",
                e.target.value === "" ? NaN : Number(e.target.value),
              )
            }
            modified={isModified(
              serverConfig.help_request.max_active_per_player,
              workingConfig.help_request.max_active_per_player,
            )}
            error={fieldErrors["help_request.max_active_per_player"]}
            aria-label="Max active per player"
          />
        </FieldRow>
      </SectionCard>

      {/* ───────── Rate limits ───────── */}
      <SectionCard
        id="rate_limits"
        title="Rate limits"
        apiName="games.config.rate_limits"
        dirty={isSectionDirty(serverConfig, workingConfig, "rate_limits")}
        description="Platform-level guardrails. Token-bucket implemented in Redis via Lua. Defaults are conservative — raise carefully."
      >
        <FieldRow
          name="Auth per IP / min"
          apiName="rate_limits.auth_per_ip_per_minute"
          hint="Brute-force protection on login + anonymous endpoints."
        >
          <NumberInputWithUnit
            unit="/min"
            min={1}
            max={10000}
            value={workingConfig.rate_limits.auth_per_ip_per_minute}
            onChange={(v) => setRate("auth_per_ip_per_minute", v)}
            modified={isModified(
              serverConfig.rate_limits.auth_per_ip_per_minute,
              workingConfig.rate_limits.auth_per_ip_per_minute,
            )}
            error={fieldErrors["rate_limits.auth_per_ip_per_minute"]}
            aria-label="Auth per IP per minute"
          />
        </FieldRow>
        <FieldRow
          name="Help-create per player / min"
          apiName="rate_limits.help_create_per_minute"
          hint="How many new help requests a single player can create per minute."
        >
          <NumberInputWithUnit
            unit="/min"
            min={1}
            max={600}
            value={workingConfig.rate_limits.help_create_per_minute}
            onChange={(v) => setRate("help_create_per_minute", v)}
            modified={isModified(
              serverConfig.rate_limits.help_create_per_minute,
              workingConfig.rate_limits.help_create_per_minute,
            )}
            error={fieldErrors["rate_limits.help_create_per_minute"]}
            aria-label="Help create per minute"
          />
        </FieldRow>
      </SectionCard>

      {/* ───────── Profile ───────── */}
      <SectionCard
        id="profile"
        title="Profile"
        apiName="games.config.profile"
        dirty={isSectionDirty(serverConfig, workingConfig, "profile")}
        description="What players can store in their profiles and which avatars are available."
      >
        <FieldRow
          name="Stats schema"
          apiName="profile.stats_schema"
          hint="Custom fields in players.stats (JSONB). Backend validates types and rejects unknown keys."
        >
          <SchemaEditor
            value={workingConfig.profile.stats_schema}
            onChange={(next) => setProfile("stats_schema", next)}
          />
        </FieldRow>
        <FieldRow
          name="Meta schema"
          apiName="profile.meta_schema"
          hint="Cosmetic profile fields in players.meta (JSONB) — avatar frame, badges, title. Backend validates the type and, when the allow-list is non-empty, the value itself."
        >
          <MetaSchemaEditor
            value={workingConfig.profile.meta_schema}
            onChange={(next) => setProfile("meta_schema", next)}
            helperText={metaSchemaHelper(workingConfig.profile.meta_schema)}
          />
        </FieldRow>
        <FieldRow
          name="Allowed avatars"
          apiName="profile.allowed_avatars"
          hint="Whitelist of asset IDs. Empty = no validation (any string accepted)."
        >
          <ChipInput
            value={workingConfig.profile.allowed_avatars}
            onChange={(next) => setProfile("allowed_avatars", next)}
            placeholder="Add asset ID…"
            modified={isModified(
              serverConfig.profile.allowed_avatars,
              workingConfig.profile.allowed_avatars,
            )}
            validate={(chip) =>
              AVATAR_REGEX.test(chip)
                ? null
                : "Letters, digits, underscore only"
            }
          />
        </FieldRow>
      </SectionCard>

      {/* ───────── Guild assets ───────── */}
      <SectionCard
        id="guild_assets"
        title="Guild assets"
        apiName="games.config.guild_assets"
        dirty={isSectionDirty(serverConfig, workingConfig, "guild_assets")}
      >
        <FieldRow
          name="Allowed logos"
          apiName="guild_assets.allowed_logos"
          hint="Whitelist of guild logo asset IDs. Empty — accept any."
        >
          <ChipInput
            value={workingConfig.guild_assets.allowed_logos}
            onChange={(next) => setGuildAssets("allowed_logos", next)}
            placeholder="Add asset ID…"
            modified={isModified(
              serverConfig.guild_assets.allowed_logos,
              workingConfig.guild_assets.allowed_logos,
            )}
            validate={(chip) =>
              AVATAR_REGEX.test(chip)
                ? null
                : "Letters, digits, underscore only"
            }
          />
        </FieldRow>
      </SectionCard>
    </>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────────

function useNewlyAdded(
  serverList: string[],
  workingList: string[],
): Set<string> {
  // Recompute each render — list is small (chip inputs); avoiding useMemo
  // here keeps the code straight and the data is referentially stable enough.
  const ref = useRef<Set<string>>(new Set());
  const server = new Set(serverList.map((s) => s.toLowerCase()));
  const next = new Set<string>();
  for (const w of workingList) {
    if (!server.has(w.toLowerCase())) next.add(w);
  }
  ref.current = next;
  return ref.current;
}

function validateConfig(cfg: ProjectConfig): FieldErrors {
  return {
    "chat.history_window":
      rangeError(cfg.chat.history_window, 50, 5000) ?? undefined,
    "chat.max_message_length":
      rangeError(cfg.chat.max_message_length, 1, 2000) ?? undefined,
    "chat.rate_limit_per_minute":
      rangeError(cfg.chat.rate_limit_per_minute, 1, 600) ?? undefined,
    "guild.max_members":
      rangeError(cfg.guild.max_members, 1, 1000) ?? undefined,
    "help_request.default_ttl_seconds":
      rangeError(cfg.help_request.default_ttl_seconds, 60, 86400) ?? undefined,
    "help_request.max_active_per_player":
      rangeError(cfg.help_request.max_active_per_player, 1, 50) ?? undefined,
    "rate_limits.auth_per_ip_per_minute":
      rangeError(cfg.rate_limits.auth_per_ip_per_minute, 1, 10000) ?? undefined,
    "rate_limits.help_create_per_minute":
      rangeError(cfg.rate_limits.help_create_per_minute, 1, 600) ?? undefined,
    "profile.meta_schema": metaSchemaError(cfg.profile.meta_schema),
  };
}

/**
 * Block Save on a malformed meta schema. MetaSchemaEditor already flags the
 * offending row inline; this lifts the same two rules to the save bar, because
 * a duplicate key silently loses one of the entries when the backend folds the
 * array into a key→field map.
 */
function metaSchemaError(
  schema: ProfileConfig["meta_schema"],
): string | undefined {
  const seen = new Set<string>();
  for (const f of schema) {
    if (!f.key) return "Every meta field needs a key.";
    if (!META_KEY_REGEX.test(f.key)) {
      return `Invalid meta key “${f.key}” — use lowercase letters, digits, underscore.`;
    }
    if (seen.has(f.key)) return `Duplicate meta key “${f.key}”.`;
    seen.add(f.key);
  }
  return undefined;
}
